import React from 'react';
import { Card, Row, Col, Typography, Space, Button } from 'antd';
import {
  TeamOutlined,
  ShopOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  ToolOutlined,
  DesktopOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  AccountBookOutlined,
  DollarOutlined,
  BankOutlined,
  UserOutlined,
  BranchesOutlined,
  ArrowRightOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

export const MastersDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const masterCategories = [
    {
      title: 'Business Partners',
      description: 'Manage customers and suppliers',
      icon: <TeamOutlined style={{ fontSize: '32px', color: '#145A54' }} />,
      color: '#E6F7FF',
      masters: [
        { label: 'Customers', path: '/growth/customers', icon: <TeamOutlined /> },
        { label: 'Suppliers', path: '/procurement/suppliers', icon: <ShopOutlined /> },
      ],
    },
    {
      title: 'Products & Services',
      description: 'Manage items and bill of materials',
      icon: <AppstoreOutlined style={{ fontSize: '32px', color: '#52c41a' }} />,
      color: '#F6FFED',
      masters: [
        { label: 'Items', path: '/inventory/items', icon: <AppstoreOutlined /> },
        { label: 'Bill of Materials', path: '/manufacturing/boms', icon: <FileTextOutlined /> },
      ],
    },
    {
      title: 'Manufacturing',
      description: 'Manage operations, workstations and shifts',
      icon: <ToolOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />,
      color: '#FFF7E6',
      masters: [
        { label: 'Operations', path: '/manufacturing/operations', icon: <ToolOutlined /> },
        { label: 'Workstations', path: '/manufacturing/workstations', icon: <DesktopOutlined /> },
        { label: 'Shifts', path: '/manufacturing/shifts', icon: <ClockCircleOutlined /> },
      ],
    },
    {
      title: 'Locations',
      description: 'Manage warehouses and storage locations',
      icon: <HomeOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      color: '#F9F0FF',
      masters: [
        { label: 'Warehouses', path: '/inventory/warehouses', icon: <HomeOutlined /> },
      ],
    },
    {
      title: 'Financial',
      description: 'Manage accounts, currencies and banks',
      icon: <DollarOutlined style={{ fontSize: '32px', color: '#13c2c2' }} />,
      color: '#E6FFFB',
      masters: [
        { label: 'Chart of Accounts', path: '/finance/chart-of-accounts', icon: <AccountBookOutlined /> },
        { label: 'Currencies', path: '/finance/currencies', icon: <DollarOutlined /> },
        { label: 'Bank Accounts', path: '/finance/bank-accounts', icon: <BankOutlined /> },
      ],
    },
    {
      title: 'Human Resources',
      description: 'Manage employees and departments',
      icon: <UserOutlined style={{ fontSize: '32px', color: '#eb2f96' }} />,
      color: '#FFF0F6',
      masters: [
        { label: 'Employees', path: '/hr/employees', icon: <UserOutlined /> },
        { label: 'Departments', path: '/hr/departments', icon: <BranchesOutlined /> },
      ],
    },
  ];

  return (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <Card>
          <Space direction="vertical" size="small">
            <Space>
              <DatabaseOutlined style={{ fontSize: '32px', color: '#145A54' }} />
              <Title level={2} style={{ margin: 0 }}>
                Masters Data Management
              </Title>
            </Space>
            <Paragraph type="secondary" style={{ margin: 0 }}>
              Centralized management for all master data across your organization
            </Paragraph>
          </Space>
        </Card>

        {/* Master Categories Grid */}
        <Row gutter={[16, 16]}>
          {masterCategories.map((category, index) => (
            <Col xs={24} sm={24} md={12} lg={8} key={index}>
              <Card
                hoverable
                style={{ height: '100%' }}
                bodyStyle={{ padding: '24px' }}
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {/* Icon & Title */}
                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '8px',
                      background: category.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {category.icon}
                  </div>

                  <div>
                    <Title level={4} style={{ marginBottom: '4px' }}>
                      {category.title}
                    </Title>
                    <Text type="secondary">{category.description}</Text>
                  </div>

                  {/* Quick Links */}
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {category.masters.map((master, idx) => (
                      <Button
                        key={idx}
                        type="text"
                        icon={master.icon}
                        onClick={() => navigate(master.path)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          height: 'auto',
                          padding: '8px 12px',
                        }}
                      >
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <span>{master.label}</span>
                          <ArrowRightOutlined style={{ fontSize: '12px' }} />
                        </Space>
                      </Button>
                    ))}
                  </Space>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Info Card */}
        <Card>
          <Space direction="vertical" size="small">
            <Title level={5}>📌 About Masters Data</Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Master data is the core business data that is essential to operations in a specific
              business or business unit. This includes data about customers, suppliers, items,
              accounts, and other business entities. Properly maintained master data ensures data
              consistency, accuracy, and reliability across all business processes.
            </Paragraph>
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default MastersDashboard;
